import i18next, { i18n, InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { canonicalLocales, getCanonicalLocale } from '~/i18n/helpers';

const LOCALISATION_NAMESPACES = process.env.LOCALISATION_NAMESPACES || [];

const DETECTION_CONFIG: InitOptions = {
  detection: {
    order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
    caches: ['localStorage'],
    lookupQuerystring: 'lang',
  },
};

interface I18NextInitOptions {
  lng?: string;
  supportedLngs?: string[] | false;
}

class I18Next {
  instance: i18n = i18next;
  isInitialized = false;
  public init(
    cb?: (detectedLang: string, platformLang: string) => void,
    options?: I18NextInitOptions,
  ): void {
    if (!this.isInitialized) {
      const builder = this.instance.use(initReactI18next);

      if (!options?.lng) {
        builder.use(LanguageDetector);
      }

      const languageConfig: InitOptions = options?.lng
        ? { lng: options.lng }
        : DETECTION_CONFIG;

      builder.use(HttpApi).init(
        {
          supportedLngs:
            options?.supportedLngs ?? Object.keys(canonicalLocales),
          fallbackLng: { ...canonicalLocales, default: ['en'] },
          ...languageConfig,
          nsSeparator: ':',
          ns: LOCALISATION_NAMESPACES,
          maxParallelReads: 100,
          defaultNS: 'general',
          interpolation: {
            escapeValue: false,
          },
          react: {
            useSuspense: false,
          },
          backend: {},
        },
        () => {
          this.isInitialized = true;

          const detectedLang = this.instance.language;
          const platformLang = getCanonicalLocale(this.instance.language);

          if (detectedLang !== platformLang) {
            this.instance.changeLanguage(platformLang).then(() => {
              cb?.(detectedLang, platformLang);
            });
          }

          this.instance.on('languageChanged', (lang) => {
            const changedDetectedLang = this.instance.language;

            const newPlatformLang = getCanonicalLocale(lang);
            cb?.(changedDetectedLang, newPlatformLang);
          });
        },
      );
    }
  }

  public t = this.instance.t;
}

export default new I18Next();
